import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { Observable, Observer, Subject, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs';
import { Task } from 'src/app/shared/models/task/task';
import { Tag } from 'src/app/shared/models/tag/tag';
import { TagResponse } from 'src/app/shared/models/tag/tag-response';
import { TagsService } from 'src/app/shared/api-services/tags.service';
import { TenantService } from 'src/app/shared/api-services/tenant.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'task-section-tags',
  templateUrl: './task-section-tags.component.html',
  styleUrls: ['./task-section-tags.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    TypeaheadModule,
  ]
})
export class TaskSectionTagsComponent implements OnInit, OnDestroy {
  @Input() task: Task;
  @Output() tagIdsChange = new EventEmitter<string[]>();

  form: FormGroup;
  tags$: Observable<string[]>;
  selectedTags: Tag[] = [];
  tenantId: string;
  
  private _destroy$ = new Subject<void>();

  constructor(
    private _tenantService: TenantService,
    private _tagsService: TagsService,
    private _translateService: TranslateService,
    private _toastrService: ToastrService,
  ) {
    this.tenantId = this._tenantService.getTenantId();
    this.form = new FormGroup({
      tag: new FormControl()
    });
  }

  ngOnInit(): void {
    this.autoComplete();
    this.loadTaskTags();
  }

  private loadTaskTags() {
    if (this.task?.tagIds?.length > 0) {
      // Uso Promise.all con un array di sottoscrizioni per caricare tutti i tag
      const tagIds = [...this.task.tagIds];
      const promises = tagIds.map(tagId => {
        return new Promise<Tag>((resolve) => {
          this._tagsService.getTag$(tagId)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
              next: (tag) => resolve(tag),
              error: () => resolve(null)
            });
        });
      });

      Promise.all(promises)
        .then(tags => {
          this.selectedTags = tags.filter(tag => tag !== null);
          this.emitTagIds();
        });
    }
  }

  autoComplete() {
    this.tags$ = new Observable((observer: Observer<string | undefined>) => {
      observer.next(this.form.controls['tag'].value);
    }).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((token: string) => this._tagsService.listTags$({ nameContains: token, limit: 5 })),
      map((tagResp: TagResponse) => {
        let tagNames: string[] = [];
        tagResp.items.forEach((tag) => {
          tagNames.push(tag.name);
        });
        return tagNames;
      }),
    );
  }

  onTagSelected(match: TypeaheadMatch): void {
    const tagName = match.value;

    let tagExists = false;
    if (this.selectedTags && this.selectedTags.length > 0) {
      tagExists = this.selectedTags.some(tag => tag.name === tagName);
    }

    if (!tagExists) {
      this._tagsService.listTags$({ name: tagName })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (tags) => {
            if (tags.items.length > 0) {
              const tag = tags.items[0];
              this.selectedTags.push(tag);
              this.emitTagIds();
              this.form.get('tag').reset();
            }
          }
        });
    } else {
      this.form.get('tag').reset();
    }
  }

  removeTag(tagId: string): void {
    this.selectedTags = this.selectedTags.filter(tag => tag.id !== tagId);
    this.emitTagIds();
  }

  private emitTagIds(): void {
    const tagIds = this.selectedTags.map(tag => tag.id);
    this.tagIdsChange.emit(tagIds);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
} 